import { Target, Users, Shield, TrendingUp, Activity } from 'lucide-react'
import { useAuth0 } from '@auth0/auth0-react'
import kinesisLogo from 'media/kinesis_logo.png'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import RadialOrbitalTimeline from 'components/ui/radial-orbital-timeline'

export default function Login() {

    const { loginWithRedirect } = useAuth0()

    const timelineData = [
        {
            id: 1,
            title: "Custom Programming",
            content: "No more juggling timelines and phases. The platform adjusts every program to each athlete's season and training load, giving you effortless precision at scale.",
            category: "Programming",
            icon: Target,
            energy: 100
        },
        {
            id: 2,
            title: "Seamless Coach tools",
            content: "Manage athletes, coaches, and billing in one place. Syncs directly with TrainingPeaks while giving you a clear view of each athlete's mechanical load.",
            category: "Tools",
            icon: Users,
            energy: 95
        },
        {
            id: 3,
            title: "Wearable Sync",
            content: "Full integration with all major wearables and platforms. Real-time training data keeps your strength work automatically calibrated to each athlete's true load.",
            category: "Integration",
            icon: Activity,
            energy: 90
        },
        {
            id: 4,
            title: "Scale smarter",
            content: "Like having a personal data analyst, strength coach, and sports scientist working 24/7 for you for each athlete. Scale your business with ease.",
            category: "Growth",
            icon: TrendingUp,
            energy: 85
        },
        {
            id: 5,
            title: "Dedicated Support",
            content: "Our fast-response support team is available to help you with any questions or issues you may have.",
            category: "Support",
            icon: Shield,
            energy: 85
        }
    ]
    
    return (
        <div className="h-screen w-full grid grid-cols-2 bg-white">

            {/* Left Side - Login Form */}
            <div className="flex flex-col justify-center bg-white py-12 relative">
                <div className='w-full max-w-2xl mx-auto px-8'>
                
                    {/* Logo/Branding */}
                    <div className="mb-10">
                        <div className="flex items-start gap-4">
                            <img 
                                src={kinesisLogo} 
                                alt="Kinesis Logo" 
                                className="h-16 w-auto"
                            />
                            
                            <div className="flex flex-col">
                                <span className="text-3xl md:text-4xl font-bold text-gray-900">Kinesis Coach Dashboard</span>
                                <p className="text-lg text-gray-500 mt-1">Strength coaching ecosystem</p>
                            </div>
                        </div>
                    </div>

                    {/* Login Button */}
                    <div className="mb-6">
                        <BrandColorButton 
                            size="large" 
                            text="Login with Coach Account" 
                            className="shadow-md"
                            action={() => loginWithRedirect()} 
                        />
                    </div>

                    {/* Sign Up Button */}
                    <div 
                        className='py-2 pr-6 flex items-center justify-center gap-x-2'
                        onClick={() => loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } })}
                    >
                        <p className='text-lg'>Don't have a Kinesis coach account?</p>
                        <h2 className='cursor-pointer text-lg text-orange-500'>Sign up</h2>
                    </div>
                </div>

                {/* Support Note */}
                <div className='absolute w-full bottom-10'>
                    <p className='text-center text-gray-600'>
                        Need help? Contact us at <a href="mailto:support@kinesisintegrated.com" className="text-blue-500 underline">support@kinesisintegrated.com</a>
                    </p>
                </div>
            </div>

            {/* Right Side - Marketing Content with Radial Timeline (50%) */}
            <div className="w-full relative bg-gradient-to-br from-orange-50 via-white to-orange-50/30 overflow-hidden">
                
                {/* Decorative Background Elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-300/20 rounded-full blur-3xl"></div>
                </div>

                {/* Content Container */}
                <div className="mt-16 relative z-10 flex flex-col items-center justify-center w-full h-full px-8 py-12 overflow-y-auto">
                    
                    {/* Radial Timeline Container - Entire block centered vertically */}
                    <div className="w-full max-w-2xl relative flex flex-col items-center">

                        {/* Heading and Subheading - Positioned immediately above the ring */}
                        <div className="text-center w-full">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Your super-powered strength coaching ecosystem
                            </h2>
                            <p className="text-lg text-gray-600">
                                Give you and your athletes an unfair advantage. Deliver completely individualized strength training at scale. Less injuries, more PRs.
                            </p>
                        </div>

                        {/* Radial Timeline */}
                        <div className="w-full h-[600px] flex items-center justify-center">
                            <RadialOrbitalTimeline
                                timelineData={timelineData}
                                backgroundColor="bg-transparent"
                                textColor="text-gray-900"
                                showHeader={false}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
